import type { FC } from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import CaseContextLine, {
  contextLineText,
} from 'src/components/SupportCase/CaseContextLine';
import { primaryChips } from 'src/components/SupportCase/stateMeta';
import { PANEL_TOOLTIP_SLOT_PROPS } from 'src/components/SupportCase/panelTooltip';
import type {
  CaseState,
  CertRef,
  JiraState,
  PropertyRef,
} from 'src/components/SupportCase/types';

/** How many characters of the title the panel shows before it truncates. */
export const TITLE_MAX_CHARS = 44;

/**
 * Cut on a word boundary, never mid-word (ENG-4299).
 *
 * CSS `text-overflow: ellipsis` cuts wherever the box ends, which produced
 * `please open a ticket with this file attach…` — a title that stops inside a word
 * reads as broken, and the panel is the narrowest place it is rendered. The CSS
 * ellipsis stays as the last resort for a single word longer than the box.
 */
export const truncateAtWord = (
  text: string,
  max: number = TITLE_MAX_CHARS
): string => {
  const clean = (text ?? '').trim().replace(/\s+/g, ' ');
  if (clean.length <= max) {
    return clean;
  }
  const cut = clean.slice(0, max);
  const boundary = cut.lastIndexOf(' ');
  // A single very long word has no boundary to use; keep the hard cut.
  const head = boundary > 0 ? cut.slice(0, boundary) : cut;
  return `${head.replace(/[\s,.;:—–-]+$/, '')}…`;
};

export type CaseHeaderVariant = 'inline' | 'stacked';

interface CaseHeaderProps {
  title: string;
  state: CaseState;
  jiraState?: JiraState;
  certification: CertRef | null;
  property: PropertyRef | null;
  /** The Jira ticket number, when the case has been filed. */
  jiraKey?: string;
  isConnected: boolean;
  fullscreen?: boolean;
  onToggleFullscreen?: () => void;
  onClose?: () => void;
  /**
   * `inline` is the case page: one row of title + meta, which has room for it.
   * `stacked` is the floating panel: title row, then meta row (ENG-4299).
   */
  variant?: CaseHeaderVariant;
}

/**
 * The case header, in the two shapes the product needs (ENG-4299).
 *
 * The panel is 420 px wide. In one row the meta alone (status chip + context +
 * ticket number) needs more than that, so the context line — the only flexible
 * item — collapsed to 67 px and wrapped to four lines while the ticket number
 * wrapped and sat on top of the fullscreen button.
 *
 * Two rules fix it, and they are structural rather than pixel tuning:
 *
 *   1. **The icons own a row.** The fullscreen and close buttons sit in a
 *      `flexShrink: 0` gutter beside the title, and the meta row is *below* them,
 *      so no reference can ever be painted under a control.
 *   2. **One element truncates.** In the meta row the context line is the only
 *      item that shrinks (`flex: 1 1 auto`, `nowrap`, ellipsis, full text in a
 *      tooltip). The chip and the ticket number are `flexShrink: 0`: the chip's
 *      words are its meaning, and the ticket number is the value a user quotes
 *      back to Support, so it must never be the thing that gets cut.
 *
 * The panel also uses the short state label (`Sent to Support` rather than
 * `Sent to Pronto Housing Support`), with the full label in the chip's tooltip.
 * That is a second label for one state and is logged as a design-system gap —
 * the words live in `stateMeta`, next to the long ones, so a rename is one edit.
 */
const CaseHeader: FC<CaseHeaderProps> = ({
  title,
  state,
  jiraState,
  certification,
  property,
  jiraKey = '',
  isConnected,
  fullscreen = false,
  onToggleFullscreen,
  onClose,
  variant = 'inline',
}) => {
  // ENG-4251 (PA-08) — one chip: the ticket state when filed, else the case state.
  const [meta] = primaryChips(state, jiraState);
  const stacked = variant === 'stacked';

  const fullTitle = (title || 'Pronto Assistant').trim().replace(/\s+/g, ' ');
  const shownTitle = stacked ? truncateAtWord(fullTitle) : fullTitle;
  const titleShortened = shownTitle !== fullTitle;

  const chip = (
    <Chip
      size="small"
      label={stacked ? meta.shortLabel : meta.label}
      color={meta.color as 'info' | 'warning' | 'success'}
      variant={state === 'awaiting_user' ? 'filled' : 'outlined'}
      data-testid="case-state-chip"
      sx={{ flexShrink: 0 }}
    />
  );

  const ticketKey = !jiraKey ? null : stacked ? (
    /*
      ENG-4220 — the ticket number, never a link. A compliance user cannot open
      Jira, so the old open-in-Jira icon was a dead end dressed as an action. The
      number is what they quote back to Support.

      ENG-4299 — in the panel it is the last item on the meta row and it never
      shrinks: `flexShrink: 0`, tabular numerals so two keys line up, and no wrap,
      because a two-line ticket number is what landed under the fullscreen button.
    */
    <Typography
      variant="caption"
      color="text.secondary"
      noWrap
      data-testid="case-jira-key"
      sx={{
        flexShrink: 0,
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {jiraKey}
    </Typography>
  ) : (
    // The case page keeps the ticket number exactly as it was.
    <Typography
      variant="caption"
      color="text.secondary"
      data-testid="case-jira-key"
    >
      {jiraKey}
    </Typography>
  );

  const offline = !isConnected ? (
    <Tooltip
      slotProps={PANEL_TOOLTIP_SLOT_PROPS}
      title="Live updates paused — reload to catch up"
    >
      {stacked ? (
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ flexShrink: 0 }}
        >
          offline
        </Typography>
      ) : (
        // The case page keeps this exactly as it was.
        <Typography variant="caption" color="text.secondary">
          offline
        </Typography>
      )}
    </Tooltip>
  ) : null;

  const toggleFullscreen = onToggleFullscreen ? (
    <Tooltip
      slotProps={PANEL_TOOLTIP_SLOT_PROPS}
      title={fullscreen ? 'Exit full screen' : 'Full screen'}
    >
      <IconButton
        size="small"
        onClick={onToggleFullscreen}
        aria-label={fullscreen ? 'Exit full screen' : 'Full screen'}
      >
        {fullscreen ? (
          <FullscreenExitIcon fontSize="small" />
        ) : (
          <FullscreenIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  ) : null;

  const close = onClose ? (
    <Tooltip slotProps={PANEL_TOOLTIP_SLOT_PROPS} title="Close">
      <IconButton
        size="small"
        onClick={onClose}
        aria-label="Close the Pronto Assistant"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ) : null;

  if (!stacked) {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 2, py: 1.5 }}
        data-testid="case-header"
      >
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography variant="subtitle1" noWrap data-testid="case-title">
            {shownTitle}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {chip}
            {/* ENG-4251 (PA-13) — one context line, names and links, never `null`. */}
            <CaseContextLine
              certification={certification}
              property={property}
              compact
            />
            {ticketKey}
            {offline}
          </Stack>
        </Box>
        {toggleFullscreen}
        {close}
      </Stack>
    );
  }

  const contextTextValue = contextLineText({ certification, property });

  return (
    <Box data-testid="case-header" sx={{ pt: 1, pb: 0.75 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ pl: 2, pr: 1, minHeight: 30 }}
        data-testid="case-header-title-row"
      >
        <Tooltip
          slotProps={PANEL_TOOLTIP_SLOT_PROPS}
          title={titleShortened ? fullTitle : ''}
        >
          <Box sx={{ minWidth: 0, flexGrow: 1, overflow: 'hidden' }}>
            <Typography variant="subtitle1" noWrap data-testid="case-title">
              {shownTitle}
            </Typography>
          </Box>
        </Tooltip>
        {/* The icons' own space: nothing from the meta row can reach it. */}
        <Stack
          direction="row"
          spacing={0.25}
          alignItems="center"
          sx={{ flexShrink: 0 }}
          data-testid="case-header-actions"
        >
          {/*
            The socket indicator lives with the controls, not with the case's
            references: at the panel's narrowest it was taking 36 px of the meta row
            and leaving the context line 5 px. It is a system state, and the gutter
            is where system state belongs.
          */}
          {offline}
          {toggleFullscreen}
          {close}
        </Stack>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="nowrap"
        sx={{ px: 2, pt: 0.5, minHeight: 24 }}
        data-testid="case-header-meta-row"
      >
        <Tooltip slotProps={PANEL_TOOLTIP_SLOT_PROPS} title={meta.label}>
          {chip}
        </Tooltip>
        <Tooltip slotProps={PANEL_TOOLTIP_SLOT_PROPS} title={contextTextValue}>
          <Box sx={{ minWidth: 0, flex: '1 1 auto', overflow: 'hidden' }}>
            <CaseContextLine
              certification={certification}
              property={property}
              compact
              singleLine
            />
          </Box>
        </Tooltip>
        {ticketKey}
      </Stack>
    </Box>
  );
};

export default CaseHeader;
